import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';
import {
  Form,
  Col,
  Button,
  Card,
  Row,
  Image,
  InputGroup,
  Alert,
  Spinner,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import md5 from 'md5';
import api from '../../../services/api';
import history from '../../../services/history';
import styles from '../../../styles.module.css';
import LaudoCardHeader from '../../../components/laudos/LaudoCardHeader';
import estenose from '../../../assets/estenose-carotida.png';
import imgcarotida from '../../../assets/carotidas.jpg';
import IcmHeaderImage from '../../../components/IcmHeaderImage';
import { getPrefix } from '../../../util/utils';
import { getScan } from '../../../services/carotidas';
import {
  INITIAL_ARTERIA,
  parseImagem,
  razaoCiCc,
  formatRazao,
} from './arteriaImagem';

export default function ImagemCarotida() {
  const { id } = useParams();
  const { data: carotida, error, isLoading } = useSWR(
    id ? ['carotida', id] : null,
    () => getScan(id)
  );
  const esquerdoRefs = useRef([]);
  const direitoRefs = useRef([]);
  const imagemCarregada = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [arteriaData, setArteriaData] = useState(null);

  useEffect(() => {
    if (!carotida || imagemCarregada.current === id) return;

    imagemCarregada.current = id;
    const parsed = parseImagem(carotida?.carotida?.[0]?.IMAGEM);
    setArteriaData(structuredClone(parsed || INITIAL_ARTERIA));
  }, [carotida, id]);

  const razaoDireita = arteriaData
    ? razaoCiCc(
        arteriaData.direita?.arteriaCarotidaInterna?.velSistolica,
        arteriaData.direita?.arteriaCarotidaComum?.velSistolica
      )
    : null;
  const razaoEsquerda = arteriaData
    ? razaoCiCc(
        arteriaData.esquerda?.arteriaCarotidaInterna?.velSistolica,
        arteriaData.esquerda?.arteriaCarotidaComum?.velSistolica
      )
    : null;
  const arteriaComRazao = arteriaData
    ? {
        ...arteriaData,
        razaoDireita: razaoDireita ?? '',
        razaoEsquerda: razaoEsquerda ?? '',
      }
    : null;

  const persistImagem = async ({ showSuccess = true } = {}) => {
    try {
      setIsSaving(true);
      const { data } = await api.put(`/carotida/${id}`, {
        arteriaData: arteriaComRazao,
      });

      if (showSuccess) {
        toast.success(data.message);
      }

      return true;
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Erro ao gravar imagem!');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleImprimirReport = async isTimbrado => {
    const isSaved = await persistImagem({ showSuccess: false });

    if (!isSaved) return;

    history.push({
      pathname: `/report-carotida/${id}`,
      state: {
        imagemData: JSON.stringify(arteriaComRazao),
        printing: isTimbrado,
      },
    });
  };

  const handleInputChange = (e, lado, artName, fieldName) => {
    const { value } = e.target;
    setArteriaData(prevData => ({
      ...prevData,
      [lado]: {
        ...prevData[lado],
        [artName]: {
          ...prevData[lado][artName],
          [fieldName]: value,
        },
      },
    }));
  };

  const handleKeyDown = (e, idx, refs) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (refs.current[idx + 1]) {
        refs.current[idx + 1].focus();
      }
    }
  };

  const renderCards = lado => {
    const refs = lado === 'esquerda' ? esquerdoRefs : direitoRefs;
    return (
      <Col sm={3} className="m-0 p-0">
        <Card style={{ marginBottom: 2, textAlign: 'center' }} bg="danger">
          <h3 style={{ color: 'white' }}>{lado.toUpperCase()}</h3>
        </Card>
        {Object.keys(arteriaData[lado]).map((arteria, index) => (
          <Card
            key={index}
            style={{ marginBottom: 2, textAlign: 'center' }}
            bg="light"
          >
            <Card.Body>
              <Card.Title style={{ color: 'green' }}>
                {arteria
                  .replace(/([A-Z])/g, ' $1')
                  .trim()
                  .toUpperCase()}
              </Card.Title>
              <div>
                <Form.Group>
                  <Form.Label>Vel Sistólica:</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Vel sistólica"
                      value={arteriaData[lado][arteria].velSistolica}
                      onChange={e =>
                        handleInputChange(e, lado, arteria, 'velSistolica')
                      }
                      ref={el => (refs.current[index * 2] = el)}
                      onKeyDown={e => handleKeyDown(e, index * 2, refs)}
                    />
                    <InputGroup.Text>cm/seg</InputGroup.Text>
                  </InputGroup>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Vel Diastólica:</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Vel diastólica"
                      value={arteriaData[lado][arteria].velDiastolica}
                      onChange={e =>
                        handleInputChange(e, lado, arteria, 'velDiastolica')
                      }
                      ref={el => (refs.current[index * 2 + 1] = el)}
                      onKeyDown={e => handleKeyDown(e, index * 2 + 1, refs)}
                    />
                    <InputGroup.Text>cm/seg</InputGroup.Text>
                  </InputGroup>
                </Form.Group>
              </div>
              <Card.Text style={{ color: 'red' }}>Fluxo laminar</Card.Text>
            </Card.Body>
          </Card>
        ))}
      </Col>
    );
  };

  if (error) {
    return (
      <Alert variant="danger" style={{ marginTop: 10 }}>
        <Alert.Heading>Erro ao carregar laudo!</Alert.Heading>
        <p>Tente novamente.</p>
      </Alert>
    );
  }

  if (isLoading || !arteriaData || !carotida) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
        }}
      >
        <Spinner animation="border" variant="dark" size="lg" />
      </div>
    );
  }

  const exame = carotida?.carotida?.[0];

  return (
    <div>
      <style>
        {`
          @media print {
            #removeMe {
              display: none;
            }
          }
        `}
      </style>

      <div className={styles.imageReport}>
        <IcmHeaderImage showBlank />
      </div>

      <LaudoCardHeader
        title={
          <>
            PACIENTE: {exame?.NOMEPAC} - IDADE: {exame?.idadePAC} anos <br />
            DATA LAUDO: {exame?.dataScanFmt} - LAUDO: {exame?.CODSCAN}
          </>
        }
      />
      <Row className="m-0 p-0">
        {renderCards('direita')}

        <Col
          sm={6}
          className="mt-5 p-0 justify-content-center align-items-center"
        >
          <Image className="m-0 p-0" fluid rounded src={imgcarotida} />

          <Row>
            <Col
              sm={12}
              className="m-0 p-3 justify-content-center align-items-center text-center"
            >
              <Image className="m-0 p-0" fluid rounded src={estenose} />
            </Col>
          </Row>
          <Row>
            <Col
              sm={12}
              className="mb-5 p-3 justify-content-center align-items-center text-center"
            >
              <Form.Label>Razão VPS CI/VPS CC</Form.Label>
              <Form.Group>
                <Form.Label>{`Direita <------------> Esquerda`}</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    value={formatRazao(razaoDireita)}
                    placeholder="Direita"
                  />
                  <Form.Control
                    type="text"
                    value={formatRazao(razaoEsquerda)}
                    placeholder="Esquerda"
                  />
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col
              sm={12}
              className="mt-5 p-5 justify-content-center align-items-center text-center"
            >
              <div
                className="mt-5 p-2"
                style={{
                  fontSize: 15,
                  color: '#449',
                }}
              >
                <i>Conferido e assinado eletronicamente por: </i>
                <br />
                <strong>
                  {getPrefix(exame?.CODREALIZOU)} {exame?.Realizou}
                </strong>
                <br></br>
                <i>
                  Assinatura Digital:{' '}
                  {md5(JSON.stringify(exame?.IMAGEM || ''))}
                </i>
              </div>
            </Col>
          </Row>
        </Col>

        {renderCards('esquerda')}
      </Row>

      <Card.Footer id="removeMe" className="m-0 p-2 d-grid gap-2 text-center">
        <Button
          variant="success"
          onClick={() => persistImagem({ showSuccess: true })}
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />{' '}
              Salvando...
            </>
          ) : (
            'Gravar'
          )}
        </Button>{' '}
        <Button
          variant="secondary"
          disabled={isSaving}
          onClick={() => handleImprimirReport(false)}
        >
          Imprimir
        </Button>{' '}
        <Button
          variant="secondary"
          disabled={isSaving}
          onClick={() => handleImprimirReport(true)}
        >
          Imprimir Timbrado
        </Button>{' '}
        <Button variant="danger" onClick={() => history.push('/agenda')}>
          Agenda
        </Button>
      </Card.Footer>
    </div>
  );
}
